import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import Checkbox from '@/shared/components/input/Checkbox';
import api from '../../../shared/api';
import config from '../../../config';
import { DeleteIcon } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';

import { CanCreate, CanDelete } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import messages from './tagSettingsMessages';

class CreditNoteReasonOverview extends Component {
  state = {
    reasons: null,
    newReason: null,
    reasonToDelete: null,
  };

  componentDidMount() {
    const { intl } = this.props;
    api.base
      .get(`${config.appUrls.api}/api/CreditNoteReason/Overview`)
      .then(({ data: reasons }) => {
        this.setState({
          reasons,
        });
      }, handleHttpError(intl));
  }

  addReason = () => {
    const { newReason, reasons } = this.state;
    const { intl } = this.props;

    if (!newReason) return;

    if (reasons.find(x => x.name.toLowerCase() === newReason.toLowerCase())) {
      ToastrService.error(intl.formatMessage(messages.CreditNoteReasonAlreadyExists));
      return;
    }

    api.base
      .post(`${config.appUrls.api}/api/CreditNoteReason`, { name: newReason })
      .then(({ data }) => {
        data.nrOfInvoicesInUse = 0;
        this.setState(state => ({
          reasons: state.reasons.find(x => x.id === data.id)
            ? state.reasons
            : [...state.reasons, data],
          newReason: null,
        }));
      }, handleHttpError(intl));
  };

  editReasonName = (reason, value) => {
    const { intl } = this.props;
    return api.base
      .put(`${config.appUrls.api}/api/CreditNoteReason`, { ...reason, name: value })
      .then(({ data }) => {
        const { name } = data;
        this.setState(state => ({
          reasons: state.reasons.map(x => (x.id === reason.id ? { ...x, name } : x)),
        }));
      }, handleHttpError(intl));
  };

  toggleDisabled = reason => {
    const { intl } = this.props;
    return api.base
      .put(`${config.appUrls.api}/api/CreditNoteReason/${reason.id}/toggle`)
      .then(({ data }) => {
        const { disabled } = data;
        this.setState(state => ({
          reasons: state.reasons.map(x => (x.id === reason.id ? { ...x, disabled } : x)),
        }));
      }, handleHttpError(intl));
  };

  deleteReason = (reason, confirmed = false) => {
    const { intl } = this.props;

    if (!confirmed) {
      this.setState({ reasonToDelete: reason });
      return null;
    }

    return api.base.delete(`${config.appUrls.api}/api/CreditNoteReason/${reason.id}`).then(() => {
      this.setState(state => ({
        reasons: state.reasons.filter(x => x.id !== reason.id),
        reasonToDelete: null,
      }));
    }, handleHttpError(intl));
  };

  render() {
    const { reasons, newReason, reasonToDelete } = this.state;
    const { intl } = this.props;

    const canUpdate = authService.hasPermission({
      functionality: 'Settings',
      privilege: privileges.Update,
    });

    return (
      <>
        {reasons ? (
          <>
            <div className="panel-heading">
              <CanCreate on="Settings">
                <div className="panel-buttons">
                  <button
                    type="button"
                    className="btn btn-inverse"
                    onClick={() => this.setState({ newReason: '' })}
                  >
                    <i className="fa fa-plus" />{' '}
                    <FormattedMessage {...messages.AddCreditNoteReason} />
                  </button>
                </div>
              </CanCreate>
            </div>
            <div className="panel-body">
              <table className="table table-grid document-tag-grid">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.Name} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.NrOfCreditNotes} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.Disabled} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.Actions} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.map(reason => (
                    <tr key={reason.id}>
                      <td>
                        <EditableTextbox
                          value={reason.name}
                          onChange={value => this.editReasonName(reason, value)}
                          maxLength="255"
                          readOnly={!canUpdate}
                        />
                      </td>

                      <td className="text-right">
                        {reason.nrOfInvoicesInUse === 0 ? (
                          0
                        ) : (
                          <span style={{ display: 'block' }} className="bold">
                            {reason.nrOfInvoicesInUse}
                          </span>
                        )}
                      </td>

                      <td className="text-right">
                        <Checkbox
                          onClick={() => this.toggleDisabled(reason)}
                          value={reason.disabled}
                          disabled={!canUpdate}
                        />
                      </td>

                      <td
                        className="text-right"
                        title={
                          reason.nrOfInvoicesInUse === 0
                            ? ''
                            : intl.formatMessage(messages.CreditNoteReasonInUse)
                        }
                      >
                        {reason.nrOfInvoicesInUse === 0 && (
                          <CanDelete on="Settings">
                            <a
                              onClick={() => this.deleteReason(reason)}
                              className="btn btn-xs btn-icon"
                            >
                              <DeleteIcon />
                            </a>
                          </CanDelete>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <OkCancelModal
                show={newReason !== null}
                title={intl.formatMessage(messages.AddCreditNoteReason)}
                onClose={() => this.setState({ newReason: null })}
                onSave={() => this.addReason()}
              >
                {newReason !== null && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={intl.formatMessage(messages.EnterCreditNoteReason)}
                    required
                    maxLength="255"
                    value={newReason}
                    onChange={e => this.setState({ newReason: e.target.value })}
                    onKeyDown={e => {
                      if (e.keyCode === 13) {
                        this.addReason();
                      } else if (e.keyCode === 27) {
                        this.setState({ newReason: null });
                      }
                    }}
                    autoFocus
                  />
                )}
              </OkCancelModal>

              <YesNoModal
                show={!!reasonToDelete}
                title={intl.formatMessage(messages.DeleteCreditNoteReason)}
                message={intl.formatMessage(messages.DeleteCreditNoteReasonConfirmationMsg)}
                onNo={() => this.setState({ reasonToDelete: null })}
                onSave={() => this.deleteReason(reasonToDelete, true)}
              />
            </div>
          </>
        ) : (
          <LoadingAnimation />
        )}
      </>
    );
  }
}

CreditNoteReasonOverview.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(CreditNoteReasonOverview);
